import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import axios from "axios";
import RiderSelectionDropdown from "./RiderSelectionDropdown";
import ThreePLPartnerDropdown from "../../../../components/ThreePLPartnerDropdown";
import {
  getPaymentState,
  PAYMENT_STATE_LABEL,
} from "../../../../utilities/paymentStatus";
import {
  MdLocalShipping,
  MdLocationOn,
  MdPerson,
  MdInfo,
  MdScale,
  MdWarning,
  MdPhone,
  MdAccessTime,
  MdCheckCircle,
  MdCancel,
  MdDirectionsBike,
  MdAssignment,
  MdGroup,
  MdUndo,
  MdSwapHoriz,
} from "react-icons/md";
import {
  updatePackageOrderDeliveryStatus,
  assignPackageOrderToDeliveryAgent,
  getOrderDetails,
} from "../../../../actions/webOrderActions";
import DropoffPrecision from "../../../../components/DropoffPrecision";
import { getAddressText, getShippingAddressText } from "../../../../utilities/orderPresentation";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import AppSelect from "../../../../components/ui/AppSelect";
import { AppConfirmDialog } from "../../../../components/ui";

const API_URL = process.env.REACT_APP_SERVER_URL;

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#F7F8FA",
  surface: "#FFFFFF",
  border: "#E4E7EC",
  borderStrong: "#C9CED6",
  text: "#111827",
  textSecondary: "#4B5563",
  textTertiary: "#9CA3AF",
  accent: "#1A56DB",
  accentSoft: "#EBF0FD",
  success: "#059669",
  successSoft: "#D1FAE5",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  danger: "#DC2626",
  dangerSoft: "#FEE2E2",
  purple: "#7C3AED",
  purpleSoft: "#EDE9FE",
  indigo: "#4338CA",
  indigoSoft: "#E0E7FF",
  shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PROCESSING: { label: "Processing", color: T.warning, soft: T.warningSoft, step: 1, icon: <MdAccessTime size={13} /> },
  PENDING_3PL: { label: "Pending 3PL", color: T.indigo, soft: T.indigoSoft, step: 2, icon: <MdGroup size={13} /> },
  ASSIGNED: { label: "Assigned", color: T.accent, soft: T.accentSoft, step: 3, icon: <MdAssignment size={13} /> },
  ACCEPTED: { label: "Accepted", color: T.purple, soft: T.purpleSoft, step: 4, icon: <MdCheckCircle size={13} /> },
  DISPATCHED: { label: "Dispatched", color: T.purple, soft: T.purpleSoft, step: 5, icon: <MdDirectionsBike size={13} /> },
  DELIVERED: { label: "Delivered", color: T.success, soft: T.successSoft, step: 6, icon: <MdCheckCircle size={13} /> },
  REJECTED: { label: "Rejected", color: T.danger, soft: T.dangerSoft, step: 0, icon: <MdCancel size={13} /> },
};

const getStatusCfg = (s) =>
  STATUS_CONFIG[s] || { label: s || "Unknown", color: T.textTertiary, soft: T.bg, step: 0, icon: <MdInfo size={13} /> };

// ─── Shared sub-components ────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = getStatusCfg(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, backgroundColor: cfg.soft, color: cfg.color, border: `1px solid ${cfg.color}30`, borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase" }}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

const SectionCard = ({ title, icon, children, style = {}, headerRight }) => (
  <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "visible", boxShadow: T.shadow, ...style }}>
    {title && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", borderBottom: `1px solid ${T.border}`, backgroundColor: T.bg }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon && <span style={{ color: T.accent, display: "flex" }}>{icon}</span>}
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.6px" }}>{title}</span>
        </div>
        {headerRight}
      </div>
    )}
    <div style={{ padding: "4px 20px 16px" }}>{children}</div>
  </div>
);

const InfoRow = ({ icon, label, value, mono = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.textTertiary, marginTop: 1, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 2 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>{value}</span>
      </div>
    </div>
  );
};

const Btn = ({ onClick, disabled, label, color, softColor, icon, loading = false, fullWidth = false }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px 18px", borderRadius: 8, border: `1.5px solid ${disabled || loading ? T.border : color}`, backgroundColor: disabled || loading ? T.bg : softColor, color: disabled || loading ? T.textTertiary : color, fontSize: 13, fontWeight: 700, cursor: disabled || loading ? "not-allowed" : "pointer", transition: "all 0.17s ease", opacity: disabled || loading ? 0.55 : 1, width: fullWidth ? "100%" : "auto", outline: "none" }}
    onMouseEnter={e => { if (!disabled && !loading) { e.currentTarget.style.backgroundColor = color; e.currentTarget.style.color = "#fff"; e.currentTarget.style.boxShadow = `0 4px 12px ${color}40`; e.currentTarget.style.transform = "translateY(-1px)"; } }}
    onMouseLeave={e => { if (!disabled && !loading) { e.currentTarget.style.backgroundColor = softColor; e.currentTarget.style.color = color; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; } }}
  >
    {loading ? <span style={{ width: 14, height: 14, border: `2px solid currentColor`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> : icon}
    {label}
  </button>
);

// ─── Delivery step pipeline ───────────────────────────────────────────────────
const PIPELINE_STEPS = [
  { key: "PROCESSING", label: "Processing", step: 1 },
  { key: "ASSIGNED", label: "Assigned", step: 3 },
  { key: "ACCEPTED", label: "Accepted", step: 4 },
  { key: "DISPATCHED", label: "Dispatched", step: 5 },
  { key: "DELIVERED", label: "Delivered", step: 6 },
];

const DeliveryPipeline = ({ status }) => {
  const current = getStatusCfg(status).step;
  const isPending3PL = status === "PENDING_3PL";
  const isRejected = status === "REJECTED";
  return (
    <div style={{ padding: "18px 0 8px" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {PIPELINE_STEPS.map((s, i) => {
          const done = current > s.step && !isRejected;
          const active = current === s.step && !isRejected;
          const isLast = i === PIPELINE_STEPS.length - 1;
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", zIndex: 1 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: isRejected ? T.border : done ? T.success : active ? T.accent : T.border, border: `2px solid ${isRejected ? T.borderStrong : done ? T.success : active ? T.accent : T.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.25s" }}>
                  {done ? <MdCheckCircle size={16} color="#fff" /> : (active && !isRejected ? <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff" }} /> : null)}
                </div>
                <span style={{ marginTop: 6, fontSize: 10, fontWeight: active ? 700 : 500, color: active ? T.text : T.textTertiary, whiteSpace: "nowrap" }}>{s.label}</span>
              </div>
              {!isLast && <div style={{ flex: 1, height: 2, backgroundColor: done && !isRejected ? T.success : T.border, marginBottom: 14, transition: "background 0.3s" }} />}
            </React.Fragment>
          );
        })}
      </div>

      {isPending3PL && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, backgroundColor: T.indigoSoft, border: `1px solid ${T.indigo}30`, borderRadius: 8, padding: "8px 12px" }}>
          <MdGroup size={16} color={T.indigo} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.indigo }}>Awaiting 3PL partner rider assignment</span>
        </div>
      )}
      {isRejected && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, backgroundColor: T.dangerSoft, border: `1px solid ${T.danger}30`, borderRadius: 8, padding: "8px 12px" }}>
          <MdCancel size={16} color={T.danger} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>This order has been cancelled</span>
        </div>
      )}
    </div>
  );
};

// ─── Assignment mode toggle ──────────────────────────────────────────────────
const ModeToggle = ({ mode, onChange, disabled }) => {
  const base = { flex: 1, padding: "9px 12px", border: "none", cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 12, borderRadius: 8, transition: "all 0.17s", letterSpacing: "0.3px" };
  return (
    <div style={{ display: "flex", gap: 6, backgroundColor: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4 }}>
      <button onClick={() => !disabled && onChange("rider")} disabled={disabled}
        style={{ ...base, backgroundColor: mode === "rider" ? T.accent : "transparent", color: mode === "rider" ? "#fff" : T.textSecondary }}>
        <MdDirectionsBike size={14} style={{ marginRight: 5, verticalAlign: "middle" }} />Direct Rider
      </button>
      <button onClick={() => !disabled && onChange("3pl")} disabled={disabled}
        style={{ ...base, backgroundColor: mode === "3pl" ? T.indigo : "transparent", color: mode === "3pl" ? "#fff" : T.textSecondary }}>
        <MdGroup size={14} style={{ marginRight: 5, verticalAlign: "middle" }} />3PL Partner
      </button>
    </div>
  );
};

// ─── Recall confirmation dialog ──────────────────────────────────────────────
const RecallModal = ({ onConfirm, onCancel, loading }) => (
  <AppConfirmDialog
    open
    title="Recall from the 3PL partner?"
    consequence={
      <>
        The order goes back to <strong>PROCESSING</strong> and the partner assignment is cleared. The 3PL partner is
        notified immediately.
      </>
    }
    confirmLabel="Recall order"
    tone="warning"
    busy={loading}
    onConfirm={onConfirm}
    onClose={onCancel}
  />
);

// ─── Main component ───────────────────────────────────────────────────────────
const PackageDeliveryManagement = () => {
  const { id: orderId } = useParams();
  const dispatch = useDispatch();

  // Rider assignment state
  const [selectedRider, setSelectedRider] = useState("");
  const [riders, setRiders] = useState([]);
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  // 3PL assignment state
  const [assignmentMode, setAssignmentMode] = useState("rider"); // "rider" | "3pl"
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [assigning3PL, setAssigning3PL] = useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [recalling3PL, setRecalling3PL] = useState(false);

  // Message
  const [message, setMessage] = useState("");

  const { order, loading, error } = useSelector((state) => state.orderDetails);
  const user = useSelector((state) => state.auth.user);

  // Live order news (a rider's pickup, a 3PL delivery, a decline, a recall)
  // through the one shared feed; see hooks/useOrderLiveUpdates.js.
  useOrderLiveUpdates(orderId, {
    onChange: (fresh, event) => {
      if (fresh?.delivery_status !== "PROCESSING") return;
      if (!fresh?.riderId) setSelectedRider("");
      if (event?.previous === "PENDING_3PL") {
        toast.info("Order recalled from 3PL. Back to Processing.");
        setAssignmentMode("rider");
      }
    },
  });

  // ── Data fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(getOrderDetails(orderId));
  }, [dispatch, orderId]);

  // ── Socket setup ────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(
      process.env.REACT_APP_SERVER_URL,
      { query: { user: user?.result?._id } },
      { transports: ["websocket"] }
    );

    socket.on("connect", () => {
      socket.emit("getRiders");
    });

    socket.on("initialRidersList", (d) => { setRiders(d); setLoadingRiders(false); });
    socket.on("updatedRidersList", setRiders);
    socket.on("riderAdded", (r) => setRiders((p) => [...p, r]));
    socket.on("riderUpdated", (r) => setRiders((p) => p.map((x) => (x._id === r._id ? r : x))));
    socket.on("riderDeleted", (r) => setRiders((p) => p.filter((x) => x._id !== r._id)));

    return () => socket.disconnect();
  }, [user?.result?._id]);

  // ── Elapsed timers ──────────────────────────────────────────────────────────
  useEffect(() => {
    let interval;
    if (order?.delivery_status === "ASSIGNED" || order?.delivery_status === "DISPATCHED") {
      interval = setInterval(() => {
        const now = Date.now();
        if (order.delivery_status === "ASSIGNED" && order.assignedAt)
          setElapsedAssignedTime(Math.floor((now - new Date(order.assignedAt)) / 1000));
        else if (order.delivery_status === "DISPATCHED" && order.dispatchedAt)
          setElapsedDispatchedTime(Math.floor((now - new Date(order.dispatchedAt)) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [order?.delivery_status, order?.assignedAt, order?.dispatchedAt]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const assignPackageOrderToRider = () => {
    if (!selectedRider) { toast.warn("Please select a rider first."); return; }
    localStorage.setItem(`order_${orderId}_assignedTime`, Date.now());
    dispatch(assignPackageOrderToDeliveryAgent(orderId, selectedRider, "ASSIGNED"));
    setMessage("Rider assignment request sent");
    toast.success("Assigning order to rider…");
  };

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !selectedRider) { toast.warn("Please select a rider before assigning."); return; }
    if (newStatus === "ASSIGNED") { assignPackageOrderToRider(); }
    else { dispatch(updatePackageOrderDeliveryStatus(orderId, newStatus)); }
  };

  const assignOrderTo3PL = async () => {
    if (!selectedPartnerId) { toast.warn("Please select a 3PL partner."); return; }
    setAssigning3PL(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/packages/${orderId}/assign-to-3pl`,
        { partnerId: selectedPartnerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message || "Order sent to 3PL partner!");
        setTimeout(() => dispatch(getOrderDetails(orderId)), 500);
      } else {
        toast.error(res.data.error?.message || "Failed to assign to 3PL partner");
      }
    } catch (err) {
      toast.error(err.response?.data?.error?.message || "Failed to assign to 3PL partner");
    } finally {
      setAssigning3PL(false);
    }
  };

  const recallFrom3PL = async () => {
    setRecalling3PL(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/packages/${orderId}/recall-from-3pl`,
        { reason: "Recalled by admin" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message || "Order recalled successfully!");
        setShowRecallModal(false);
        setAssignmentMode("rider");
        setTimeout(() => dispatch(getOrderDetails(orderId)), 500);
      } else {
        toast.error(res.data.error?.message || "Failed to recall order");
      }
    } catch (err) {
      toast.error(err.response?.data?.error?.message || "Failed to recall order from 3PL");
    } finally {
      setRecalling3PL(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const status = order?.delivery_status;
  const isPending3PL = status === "PENDING_3PL";
  const isTerminal = status === "DELIVERED" || status === "REJECTED";
  const canAssign = status === "PROCESSING";

  // ── Loading / Error ─────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320, color: T.textTertiary, fontSize: 14, gap: 10 }}>
      <span style={{ width: 20, height: 20, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
      Loading order…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ margin: 24, padding: "16px 20px", backgroundColor: T.dangerSoft, border: `1px solid ${T.danger}30`, borderRadius: 10, color: T.danger, fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
      <MdCancel size={18} />{error}
    </div>
  );

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {showRecallModal && (
        <RecallModal
          onConfirm={recallFrom3PL}
          onCancel={() => setShowRecallModal(false)}
          loading={recalling3PL}
        />
      )}

      <div style={{ backgroundColor: T.bg, minHeight: "100vh", padding: "28px 24px", fontFamily: "'Inter', system-ui, sans-serif" }}>

        {/* ── Page Header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MdLocalShipping size={22} color={T.accent} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text, letterSpacing: "-0.3px" }}>Package Delivery</h1>
              <p style={{ margin: 0, fontSize: 12, color: T.textTertiary, fontFamily: "monospace", marginTop: 2 }}>#{orderId?.slice(-12)?.toUpperCase()}</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {message && (
              <span style={{ fontSize: 12, fontWeight: 600, color: T.accent, backgroundColor: T.accentSoft, border: `1px solid ${T.accent}30`, borderRadius: 6, padding: "5px 10px" }}>{message}</span>
            )}
            <StatusBadge status={status} />
          </div>
        </div>

        {/* ── Body grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

          {/* ── LEFT column ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Delivery progress pipeline */}
            <SectionCard title="Delivery Progress" icon={<MdLocalShipping size={14} />}>
              <DeliveryPipeline status={status} />
            </SectionCard>

            {/* Package details */}
            <SectionCard title="Package Details" icon={<MdInfo size={14} />}>
              <InfoRow icon={<MdAssignment size={17} />} label="Order ID" value={order?._id} mono />
              <InfoRow icon={<MdPerson size={17} />} label="Customer Contact" value={order?.shippingAddress?.customerNumber} />
              {order?.shippingAddress?.recipientPhone && <InfoRow icon={<MdPhone size={17} />} label="Recipient Phone" value={order.shippingAddress.recipientPhone} />}
              {order?.pickupAddress?.senderPhone && <InfoRow icon={<MdPhone size={17} />} label="Sender Phone" value={order.pickupAddress.senderPhone} />}
              <InfoRow icon={<MdLocationOn size={17} color="#EF4444" />} label="Pickup Address" value={getAddressText(order?.pickupAddress?.address)} />
              <InfoRow icon={<MdLocationOn size={17} color="#059669" />} label="Delivery Address" value={getShippingAddressText(order?.shippingAddress)} />
              {order?.package?.description && <InfoRow icon={<MdInfo size={17} />} label="Package Description" value={order.package.description} />}
              {order?.package?.specialInstructions && <InfoRow icon={<MdInfo size={17} color={T.warning} />} label="Special Instructions" value={order.package.specialInstructions} />}
              <InfoRow icon={<MdScale size={17} />} label="Package Weight" value={order?.package?.weight ? `${order.package.weight} kg` : null} />
              {order?.package?.isFragile && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, backgroundColor: T.dangerSoft, border: `1px solid ${T.danger}25`, borderRadius: 8, padding: "8px 12px" }}>
                  <MdWarning size={16} color={T.danger} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.danger }}>Fragile — Handle with care</span>
                </div>
              )}
              <InfoRow icon={<MdInfo size={17} />} label="Payment Method" value={order?.paymentMethod} />
              {order?.totalPrice != null && (
                <div style={{ marginTop: 12, padding: "12px 14px", backgroundColor: T.bg, borderRadius: 10, border: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Delivery Fee</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: T.text }}>UGX {order.totalPrice.toLocaleString()}</span>
                </div>
              )}
            </SectionCard>

            {/* Status update */}
            <SectionCard title="Update Order Status" icon={<MdAccessTime size={14} />}>
              <div style={{ paddingTop: 8 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Select New Status</label>
                <AppSelect
                  value={status || ""}
                  onChange={(e) => handleUpdateStatus(e.target.value)}
                  disabled={isTerminal || isPending3PL}
                  style={{ width: "100%", padding: "11px 36px 11px 14px", borderRadius: 8, border: `1.5px solid ${T.border}`, backgroundColor: T.surface, fontSize: 14, fontWeight: 600, color: T.text, cursor: isTerminal || isPending3PL ? "not-allowed" : "pointer", outline: "none", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234B5563' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
                >
                  <option value="PROCESSING">1 · Processing</option>
                  <option value="ASSIGNED">2 · Assigned to Rider</option>
                  <option value="DISPATCHED">3 · Dispatched</option>
                  <option value="DELIVERED">4 · Delivered</option>
                  <option value="REJECTED">5 · Cancel Order</option>
                </AppSelect>
                {isPending3PL && (
                  <p style={{ marginTop: 8, fontSize: 12, color: T.indigo, fontWeight: 600 }}>
                    ⓘ  Status updates are locked while order is with a 3PL partner. Recall the order to regain control.
                  </p>
                )}
              </div>
              {/* Quick action buttons */}
              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <Btn label="Dispatch" onClick={() => handleUpdateStatus("DISPATCHED")} disabled={status !== "ASSIGNED"} color={T.purple} softColor={T.purpleSoft} icon={<MdDirectionsBike size={16} />} />
                <Btn label="Mark Delivered" onClick={() => handleUpdateStatus("DELIVERED")} disabled={status !== "DISPATCHED"} color={T.success} softColor={T.successSoft} icon={<MdCheckCircle size={16} />} />
                <Btn label="Cancel Order" onClick={() => handleUpdateStatus("REJECTED")} disabled={isTerminal || isPending3PL} color={T.danger} softColor={T.dangerSoft} icon={<MdCancel size={16} />} />
              </div>
            </SectionCard>
          </div>

          {/* ── RIGHT column ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* ── Assignment panel ── */}
            <SectionCard
              title="Assign Delivery"
              icon={<MdSwapHoriz size={14} />}
              headerRight={isPending3PL ? (
                <Btn
                  label="Recall from 3PL"
                  onClick={() => setShowRecallModal(true)}
                  loading={recalling3PL}
                  disabled={false}
                  color={T.warning}
                  softColor={T.warningSoft}
                  icon={<MdUndo size={14} />}
                />
              ) : null}
            >
              <div style={{ paddingTop: 10 }}>
                {/* If order is already with 3PL */}
                {isPending3PL ? (
                  <div style={{ padding: "14px 16px", backgroundColor: T.indigoSoft, border: `1px solid ${T.indigo}30`, borderRadius: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <MdGroup size={18} color={T.indigo} />
                      <span style={{ fontWeight: 700, fontSize: 14, color: T.indigo }}>Order with 3PL Partner</span>
                    </div>
                    {order?.assignmentDetails?.partnerId && (
                      <p style={{ margin: 0, fontSize: 13, color: T.textSecondary }}>
                        Partner: <strong>{order.assignmentDetails.partnerId?.companyName || order.assignmentDetails.partnerId}</strong>
                      </p>
                    )}
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: T.textTertiary }}>
                      Waiting for the 3PL partner to assign a rider. Use "Recall from 3PL" to take back control.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Mode toggle — only when order is PROCESSING */}
                    <ModeToggle
                      mode={assignmentMode}
                      onChange={setAssignmentMode}
                      disabled={!canAssign}
                    />

                    {!canAssign && (
                      <p style={{ marginTop: 8, fontSize: 12, color: T.textTertiary }}>
                        Assignment is available only when order is in <strong>Processing</strong> status.
                      </p>
                    )}

                    <div style={{ marginTop: 14 }}>
                      {assignmentMode === "rider" ? (
                        /* ── Direct rider assignment ── */
                        <RiderSelectionDropdown
                          order={order}
                          riderId={selectedRider}
                          setRiderId={setSelectedRider}
                          loadingRiders={loadingRiders}
                          riders={riders}
                          assignRestaurantOrderToRider={assignPackageOrderToRider}
                          elapsedAssignedTime={elapsedAssignedTime}
                          elapsedDispatchedTime={elapsedDispatchedTime}
                        />
                      ) : (
                        /* ── 3PL partner assignment ── */
                        <div>
                          <ThreePLPartnerDropdown
                            dropoff={order?.shippingAddress?.latLng}
                            selectedPartnerId={selectedPartnerId}
                            onSelectPartner={setSelectedPartnerId}
                            disabled={!canAssign}
                            showLabel={false}
                          />
                          <Btn
                            label="Send to 3PL Partner"
                            onClick={assignOrderTo3PL}
                            loading={assigning3PL}
                            disabled={!canAssign || !selectedPartnerId}
                            color={T.indigo}
                            softColor={T.indigoSoft}
                            icon={<MdGroup size={15} />}
                            fullWidth
                          />
                          {canAssign && selectedPartnerId && (
                            <p style={{ marginTop: 8, fontSize: 12, color: T.textTertiary, lineHeight: 1.5 }}>
                              The order will move to <strong>PENDING_3PL</strong> status. The partner will be notified and will assign one of their riders.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </SectionCard>

            {/* GPS coords */}
            {(order?.pickupAddress?.latLng || order?.shippingAddress?.latLng) && (
              <SectionCard title="GPS Coordinates" icon={<MdLocationOn size={14} />}>
                {order?.pickupAddress?.latLng?.lat && (
                  <div style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 3 }}>Pickup Coords</span>
                    <span style={{ fontFamily: "monospace", fontSize: 12, color: T.textSecondary }}>{order.pickupAddress.latLng.lat?.toFixed(7)}, {order.pickupAddress.latLng.lng?.toFixed(7)}</span>
                  </div>
                )}
                {order?.shippingAddress?.latLng?.lat && (
                  <div style={{ padding: "10px 0" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 3 }}>Delivery Coords</span>
                    <span style={{ fontFamily: "monospace", fontSize: 12, color: T.textSecondary }}>{order.shippingAddress.latLng.lat?.toFixed(7)}, {order.shippingAddress.latLng.lng?.toFixed(7)}</span>
                    <div style={{ marginTop: 6 }}><DropoffPrecision shippingAddress={order.shippingAddress} compact /></div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Order metadata */}
            <SectionCard title="Order Metadata" icon={<MdInfo size={14} />}>
              <div style={{ paddingTop: 4 }}>
                {[
                  { label: "Created", value: order?.createdAt ? new Date(order.createdAt).toLocaleString() : null },
                  { label: "Updated", value: order?.updatedAt ? new Date(order.updatedAt).toLocaleString() : null },
                  { label: "Order Source", value: order?.orderSrc },
                  // Derived from the canonical payment fields — the raw
                  // `payment_status` column is unset on virtually every order,
                  // so this row simply vanished (the .filter below drops
                  // undefined values) instead of reporting payment state.
                  { label: "Payment Status", value: PAYMENT_STATE_LABEL[getPaymentState(order)] },
                  { label: "Assignment Method", value: order?.assignmentDetails?.assignmentMethod },
                ].filter((m) => m.value).map((m) => (
                  <div key={m.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.textTertiary, textTransform: "uppercase", letterSpacing: "0.4px" }}>{m.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary }}>{m.value}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </>
  );
};

export default PackageDeliveryManagement;
